import type {
  SystemWorkspaceDefinition,
} from './SystemWorkspaceContracts';

export const membershipBillingSystemWorkspace:
  SystemWorkspaceDefinition = {
    id: 'membership-billing',
    name: 'Membership & Billing',
    description:
      'Governs membership purchase, Stripe recovery, entitlement persistence, billing evidence, receipts, and owner-inclusive seat capacity.',
    owningGovernor: 'Membership Governor',
    authoritySummary:
      'Stripe active or trialing subscription must agree with the authoritative Supabase membership entitlement.',
    health: 'unknown',

    statusSignals: {
      criticalFailure: false,
      simulationRunning: false,
      repairRunning: false,
      currentEvidenceAvailable: true,
      nonCriticalIssue: true,
      reason:
        'Source and build are certified; simulated runtime, deployed runtime, live transaction, and recovery certification remain pending.',
    },

    authoritySources: [
      {
        id: 'membership-modal',
        kind: 'frontend',
        label: 'Membership checkout and receipt presentation',
        reference:
          'src/components/MembershipModal.tsx',
        authoritative: false,
      },
      {
        id: 'membership-edge-function',
        kind: 'edge_function',
        label: 'Membership Stripe authority',
        reference:
          'supabase/functions/kinsvault-subscription/index.ts',
        authoritative: true,
      },
      {
        id: 'membership-entitlements',
        kind: 'database_table',
        label: 'Membership entitlement authority',
        reference:
          'public.membership_entitlements',
        authoritative: true,
      },
      {
        id: 'membership-entitlement-rpc',
        kind: 'database_rpc',
        label: 'Stripe entitlement persistence authority',
        reference:
          'public.upsert_membership_entitlement_from_stripe',
        authoritative: true,
      },
      {
        id: 'billing-purchase-attempts',
        kind: 'database_table',
        label: 'Billing attempt and receipt evidence',
        reference:
          'public.billing_purchase_attempts',
        authoritative: true,
      },
      {
        id: 'stripe',
        kind: 'external_service',
        label: 'Subscription and invoice authority',
        reference: 'Stripe',
        authoritative: true,
      },
    ],

    dependencies: [
      {
        systemId: 'authentication',
        relationship: 'required',
        reason:
          'Membership ownership requires an authenticated user.',
      },
      {
        systemId: 'database',
        relationship: 'required',
        reason:
          'Entitlement and billing evidence must persist.',
      },
      {
        systemId: 'sponsored-seats',
        relationship: 'downstream',
        reason:
          'Membership capacity determines sponsored-seat availability.',
      },
      {
        systemId: 'kinsspace',
        relationship: 'shared',
        reason:
          'Billing ownership and family-circle storage authority remain separate but related.',
      },
    ],

    certification: {
      source: 'pass',
      build: 'pass',
      simulatedRuntime: 'not_run',
      deployedRuntime: 'pending',
      liveTransaction: 'pending',
      recovery: 'pending',
    },

    simulatorPolicy: {
      enabled: true,
      productionWritesAllowed: false,
      requiresExplicitLiveApproval: true,
      evidenceNamespace:
        'simulated:membership-billing',
      supportedModes: [
        'SIMULATED',
        'REPLAYED',
        'HISTORICAL',
      ],
      resetRequired: true,
    },

    aiPolicy: {
      enabled: true,
      maximumAutonomousLevel: 3,
      allowedLevels: [0, 1, 2, 3],
      mayReadEvidence: true,
      mayGenerateScenarios: true,
      mayRecommendRepairs: true,
      mayPrepareChanges: true,
      mayExecuteProductionWrites: false,
      mustCiteEvidence: true,
      constitutionOverridesRecommendations: true,
    },

    milestone: {
      current:
        'CC-SYS-001R — Governed repair orchestrator',
      completed: [
        'BP-004E2A',
        'BP-004E2B1',
        'BP-004E2B2',
        'BP-004E2C',
        'BP-004E2D',
        'CC-SYS-001A',
        'CC-SYS-001B',
        'CC-SYS-001C',
        'CC-SYS-001D',
        'CC-SYS-001E',
        'CC-SYS-001F',
      ],
      pending: [],
    },
  };
